import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Globe, BookOpen, Plus, Loader2, Sparkles, Eye, BookText, MessageSquare } from 'lucide-react';
import { libraryService } from '../services/libraryService';
import { getLanguageName } from '../services/languageConstants';
import { translationService } from '../services/translationService';
import { RatingBadge } from '../components/RatingStars';

const getLanguageCode = (lang: string): string => {
  if (!lang) return '';
  const normalized = lang.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const aliases: Record<string, string> = {
    'ingles': 'en', 'english': 'en', 'us': 'en', 'en us': 'en', 'en-us': 'en', 'en-gb': 'en',
    'frances': 'fr', 'french': 'fr', 'fr-fr': 'fr',
    'alemao': 'de', 'german': 'de', 'de-de': 'de',
    'espanhol': 'es', 'spanish': 'es', 'es-es': 'es',
    'italiano': 'it', 'italian': 'it', 'it-it': 'it',
    'portugues': 'pt', 'portuguese': 'pt', 'pt-br': 'pt', 'pt-pt': 'pt',
    'holandes': 'nl', 'dutch': 'nl', 'nl-nl': 'nl',
    'japones': 'ja', 'japanese': 'ja', 'ja-jp': 'ja',
    'russo': 'ru', 'russian': 'ru', 'ru-ru': 'ru',
    'chines': 'zh', 'chinese': 'zh', 'zh-cn': 'zh'
  };
  return aliases[normalized] || normalized.split(/[-_ ]/)[0];
};

const getTranslatedDifficulty = (level: string | number | undefined, t: any): string => {
  if (level === undefined || level === null) return t('reader.difficulty_none', 'Nível: --');
  const cefr = libraryService.mapReadingLevelToCefr(level);
  if (cefr.includes('A1')) return t('reader.level_a1', 'Iniciante - A1');
  if (cefr.includes('A2')) return t('reader.level_a2', 'Básico - A2');
  if (cefr.includes('B1')) return t('reader.level_b1', 'Intermediário - B1');
  if (cefr.includes('B2')) return t('reader.level_b2', 'Avançado - B2');
  if (cefr.includes('C1')) return t('reader.level_c1', 'Fluente - C1');
  if (cefr.includes('C2')) return t('reader.level_c2', 'Mestre - C2');
  return cefr;
};

interface CustomCatalogBook {
  id: string;
  title: string;
  author: string;
  language: string;
  cover: string;
  file: string;
  format: 'epub' | 'txt' | 'html' | 'pdf';
  description?: string;
  reading_level?: string | number;
  subjects?: string[];
}

interface Props {
  book: CustomCatalogBook;
  onClose: () => void;
  onImport: (book: CustomCatalogBook) => void;
  onPreview: (book: CustomCatalogBook) => void;
  isImporting: boolean;
  languageFlags: Record<string, string>;
  renderCover: (book: { title: string, language?: string }) => React.ReactNode;
  primaryLabel?: string;
  secondaryLabel?: string;
  primaryIcon?: React.ReactNode;
  secondaryIcon?: React.ReactNode;
  ratingAvg?: number | null;
  ratingCount?: number;
}

const BookDetailsModal: React.FC<Props> = ({
  book,
  onClose,
  onImport,
  onPreview,
  isImporting,
  languageFlags,
  renderCover,
  primaryLabel,
  secondaryLabel,
  primaryIcon = <Plus size={20} />,
  secondaryIcon = <Eye size={20} />,
  ratingAvg,
  ratingCount
}) => {
  const { t } = useTranslation();
  const finalPrimaryLabel = primaryLabel || t('library.saveToShelf');
  const finalSecondaryLabel = secondaryLabel || t('library.previewSample');

  const [translatedDescription, setTranslatedDescription] = useState<string | null>(null);
  const [isTranslatingDesc, setIsTranslatingDesc] = useState<boolean>(false);

  const handleTranslateDescription = async () => {
    if (translatedDescription) {
      setTranslatedDescription(null);
      return;
    }
    if (!book.description) return;
    setIsTranslatingDesc(true);
    try {
      const nativeLang = (localStorage.getItem('i18nextLng') || 'pt').split('-')[0].toLowerCase();
      const sourceCode = getLanguageCode(book.language) || 'en';

      const placeholders: string[] = [];
      // Protect any text within double quotes, single quotes, typographic quotes, and guillemets: "...", '...', “...”, ‘...’, «...»
      const protectedText = book.description.replace(/(["'“‘«])([\s\S]*?)(["'”’»])/g, (match) => {
        placeholders.push(match);
        return `__BOOK_QUOTE_TOKEN_${placeholders.length - 1}__`;
      });

      const res = await translationService.unifiedTranslate(protectedText, nativeLang, undefined, sourceCode);
      if (res && res.translation) {
        let finalTranslation = res.translation;
        placeholders.forEach((orig, idx) => {
          const tokenRegex = new RegExp(`__BOOK_QUOTE_TOKEN_${idx}__`, 'gi');
          finalTranslation = finalTranslation.replace(tokenRegex, orig);
        });
        setTranslatedDescription(finalTranslation);
      }
    } catch (e) {
      console.warn('Erro ao traduzir descrição do livro:', e);
    } finally {
      setIsTranslatingDesc(false);
    }
  };

  useEffect(() => {
    const originalStyle = document.body.style.overflow;
    const originalHeight = document.body.style.height;

    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';

    return () => {
      document.body.style.overflow = originalStyle;
      document.body.style.height = originalHeight;
    };
  }, []);

  const levelSource = (book as any).reading_level || (book as any).difficulty_level;
  const langCode = getLanguageCode(book.language);

  return (
    <div
      className="fixed inset-0 w-screen h-screen flex items-center justify-center bg-black/60 backdrop-blur-md animate-in fade-in duration-300"
      style={{
        zIndex: 200,
        margin: 0,
        padding: '1rem',
        pointerEvents: 'auto',
        left: 0,
        top: 0
      }}
      onClick={onClose}
    >
      <div
        className="bg-surface w-full max-w-4xl rounded-[32px] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-500 relative flex flex-col md:flex-row border border-black/[0.06] dark:border-white/[0.08] shadow-[0_40px_100px_-40px_rgba(0,0,0,0.6)]"
        style={{
          maxHeight: '88vh',
          display: 'flex',
          pointerEvents: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-primary/[0.06] hover:bg-primary hover:text-slate-900 text-ink rounded-full z-[70] flex items-center justify-center transition-colors"
          aria-label="Fechar detalhes"
        >
          <X size={18} strokeWidth={1.75} />
        </button>

        {/* Capa: o livro "flutua" sobre um palco em tom pastel */}
        <div className="w-full md:w-[42%] flex-shrink-0 bg-tint-stone relative overflow-hidden flex items-center justify-center py-8 md:py-12">
          <span aria-hidden className="absolute -top-20 -left-16 w-72 h-72 rounded-full bg-tint-lavender/70 blur-3xl" />
          <span aria-hidden className="absolute -bottom-24 -right-10 w-72 h-72 rounded-full bg-tint-peach/60 blur-3xl" />
          <div className="relative w-36 md:w-56 aspect-[2/3] rounded-[18px] overflow-hidden shadow-[0_30px_60px_-24px_rgba(0,0,0,0.55)] rotate-[-2deg] transition-transform duration-700 hover:rotate-0 hover:scale-[1.03]">
            {renderCover(book)}
          </div>
        </div>

        <div className="flex-1 p-6 md:p-10 flex flex-col gap-6 overflow-y-auto custom-scrollbar">
          <div className="pr-10">
            <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted flex items-center gap-2">
              <BookText size={13} strokeWidth={1.75} /> livro
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-ink leading-[1.08] tracking-[-0.035em] mt-2">
              {book.title}<span className="text-primary">.</span>
            </h2>
            {book.author && <p className="text-muted text-[15px] mt-2">{book.author}</p>}
            {ratingAvg ? <RatingBadge avg={ratingAvg} count={ratingCount} className="mt-3 text-[13px]" /> : null}
          </div>

          {/* Idioma e nível */}
          <div id="tour-book-info" className="flex flex-wrap items-center gap-2">
            <span className="h-9 pl-2 pr-3.5 rounded-full bg-tint-sky text-ink text-sm flex items-center gap-2">
              <span className="text-base leading-none">{languageFlags[book.language?.toLowerCase()] || '🌐'}</span>
              {t('languages.' + langCode, getLanguageName(book.language))}
            </span>
            <span className="h-9 px-3.5 rounded-full bg-tint-sage text-ink text-sm flex items-center gap-2">
              {getTranslatedDifficulty(levelSource, t)}
            </span>
            {book.subjects && book.subjects.length > 0 && libraryService.filterTags(book.subjects).slice(0, 6).map((tag, idx) => (
              <span key={idx} className="h-7 px-3 rounded-full bg-ink/[0.05] text-[11px] text-ink/70 flex items-center">
                {tag}
              </span>
            ))}
          </div>

          {/* Descrição */}
          <div className="bg-ink/[0.03] dark:bg-white/[0.03] rounded-[22px] p-5 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] uppercase font-mono tracking-[0.14em] text-muted">sobre o livro</span>
              {book.description && (
                <button
                  onClick={handleTranslateDescription}
                  disabled={isTranslatingDesc}
                  className={`h-8 pl-2.5 pr-3 rounded-full text-xs flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-60 ${translatedDescription ? 'bg-ink text-surface' : 'bg-ink/[0.06] hover:bg-ink/[0.1] text-ink'}`}
                >
                  {isTranslatingDesc ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} strokeWidth={1.75} />}
                  <span>{isTranslatingDesc ? 'Traduzindo...' : translatedDescription ? 'Ver original' : 'Traduzir'}</span>
                </button>
              )}
            </div>
            <div className="max-h-40 overflow-y-auto custom-scrollbar pr-2" id="tour-book-description">
              {book.description ? (
                <p className="text-[14px] text-ink/75 leading-relaxed">{translatedDescription || book.description}</p>
              ) : (
                <p className="text-sm text-muted italic leading-relaxed">{t('library.no_description', 'Nenhuma descrição disponível para este livro.')}</p>
              )}
            </div>
          </div>

          <div className="mt-auto pt-1">
            <button
              id="tour-book-action-btn"
              onClick={() => onImport(book)}
              disabled={isImporting}
              className="group w-full h-14 pl-6 pr-2 bg-primary text-slate-900 rounded-full text-sm font-medium flex items-center justify-between gap-3 hover:scale-[1.01] active:scale-95 transition-transform disabled:opacity-50"
            >
              {isImporting ? (
                <span className="flex items-center gap-3"><Loader2 className="animate-spin" size={18} /> {t('library.processing', 'Processando...')}</span>
              ) : (
                <>
                  <span>{finalPrimaryLabel}</span>
                  <span className="w-10 h-10 rounded-full bg-surface/15 flex items-center justify-center [&>svg]:w-[17px] [&>svg]:h-[17px] transition-transform duration-300 group-hover:translate-x-0.5">{primaryIcon}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookDetailsModal;
